import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, serverTimestamp, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import Navigation from '../components/Navigation';
import CSVUpload from '../components/CSVUpload';
import './AdminProjects.css';

function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: [''],
    category: '',
    location: '',
    budget: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  async function fetchProjects() {
    try {
      const q = query(collection(db, 'projects'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const projectsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProjects(projectsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }

  function handleRequirementChange(index, value) {
    const newRequirements = [...formData.requirements];
    newRequirements[index] = value;
    setFormData(prev => ({
      ...prev,
      requirements: newRequirements
    }));
  }

  function addRequirement() {
    setFormData(prev => ({
      ...prev,
      requirements: [...prev.requirements, '']
    }));
  }

  function removeRequirement(index) {
    const newRequirements = formData.requirements.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      requirements: newRequirements
    }));
  }

  function handleEdit(project) {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      requirements: project.requirements || [''],
      category: project.category,
      location: project.location,
      budget: project.budget || ''
    });
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      requirements: [''],
      category: '',
      location: '',
      budget: ''
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    try {
      const projectData = {
        ...formData,
        budget: parseInt(formData.budget),
        requirements: formData.requirements.filter(req => req.trim() !== ''),
        updatedAt: serverTimestamp()
      };

      if (editingProject) {
        // Update existing project
        await updateDoc(doc(db, 'projects', editingProject.id), projectData);
        alert('Project updated successfully!');
      } else {
        // Create new project
        await addDoc(collection(db, 'projects'), {
          ...projectData,
          status: 'available',
          claimedBy: null,
          claimedDate: null,
          createdAt: serverTimestamp()
        });
        alert('Project created successfully! Notifications sent to all chapters.');
      }
      
      handleCancel();
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Failed to save project. Please try again.');
    }
  }

  async function handleDelete(projectId) {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'projects', projectId));
      alert('Project deleted successfully!');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project.');
    }
  }

  async function handleStatusChange(projectId, newStatus) {
    try {
      await updateDoc(doc(db, 'projects', projectId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      fetchProjects();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  }

  return (
    <div className="admin-container">
      <Navigation />
      
      <div className="admin-content">
        <div className="admin-header">
          <h1>Manage Projects ({projects.length})</h1>
          <div className="header-controls">
            <button onClick={() => setShowCSVUpload(!showCSVUpload)} className="csv-button">
              {showCSVUpload ? 'Hide CSV Upload' : '📤 CSV Upload'}
            </button>
            <button onClick={() => setShowForm(!showForm)} className="create-button">
              {showForm ? 'Cancel' : '+ Create New Project'}
            </button>
          </div>
        </div>

        {showCSVUpload && (
          <CSVUpload type="projects" onUploadComplete={fetchProjects} />
        )}

        {showForm && (
          <div className="project-form-card">
            <h2>{editingProject ? 'Edit Project' : 'Create New Project'}</h2>
            <form onSubmit={handleSubmit} className="project-form">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  <option value="Infrastructure & Facility Upgrades">Infrastructure & Facility Upgrades</option>
                  <option value="Digital Learning & Smart Education">Digital Learning & Smart Education</option>
                  <option value="Student Welfare & Essential Support">Student Welfare & Essential Support</option>
                  <option value="ABC & Education Support">ABC & Education Support</option>
		  <option value="Women Empowerment">ABC & Education Support</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Location/District *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Budget ($) *</label>
                  <input
                    type="number"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    required
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Requirements *</label>
                {formData.requirements.map((req, index) => (
                  <div key={index} className="requirement-row">
                    <input
                      type="text"
                      value={req}
                      onChange={(e) => handleRequirementChange(index, e.target.value)}
                      placeholder={`Requirement ${index + 1}`}
                    />
                    {formData.requirements.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeRequirement(index)}
                        className="remove-button"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={addRequirement}
                  className="add-requirement-button"
                >
                  + Add Requirement
                </button>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-button">
                  {editingProject ? 'Update Project' : 'Create Project'}
                </button>
                <button type="button" onClick={handleCancel} className="cancel-form-button">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="projects-table-container">
          <h2>All Projects ({projects.length})</h2>
          {loading ? (
            <div className="loading">Loading projects...</div>
          ) : (
            <table className="projects-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Location</th>
                  <th>Budget</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(project => (
                  <tr key={project.id}>
                    <td className="title-cell">{project.title}</td>
                    <td>{project.category}</td>
                    <td>{project.location}</td>
                    <td>${project.budget?.toLocaleString() || 'TBD'}</td>
                    <td>
                      <select
                        value={project.status}
                        onChange={(e) => handleStatusChange(project.id, e.target.value)}
                        className="status-select"
                      >
                        <option value="available">Available</option>
                        <option value="claimed">Claimed</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td className="actions-cell">
                      <button 
                        onClick={() => handleEdit(project)}
                        className="edit-button"
                        title="Edit Project"
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(project.id)}
                        className="delete-button"
                        title="Delete Project"
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminProjects;
